import React, { useEffect, useMemo, useState } from "react"
import moment from "moment-timezone"
import { X, Lock, Check } from "lucide-react"
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js"

import { stripePromise } from "../../utils/stripe"
import { funEU } from "../../utils/firebase"

const gbp = (value) => `£${Math.round(Number(value) || 0).toLocaleString("en-GB")}`
const gbp2 = (value) =>
	Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const createQuoteDepositIntentCallable = funEU.httpsCallable("createQuoteDepositIntent")

/**
 * "Secure Quote & Book" modal. Recaps the trip, collects the lead booker's
 * details, then takes the deposit via Stripe. On a successful payment the
 * webhook converts the enquiry into a booking and writes the deposit onto it;
 * the public page's live listener then flips to its "booked" state.
 *
 * The amount shown here is the display deposit; the amount actually charged is
 * recomputed server-side by createQuoteDepositIntent from the real quote.
 */
export default function BookModal({ quote, deposit, onClose }) {
	const [payer, setPayer] = useState({ firstName: "", lastName: "", email: "" })
	const [stage, setStage] = useState("details") // details -> payment -> done

	// Close on Escape (but not mid-success - let them read the confirmation).
	useEffect(() => {
		const onKey = (event) => event.key === "Escape" && onClose()
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [onClose])

	const hotelTitle = [...new Set(quote.hotel_names || [])].join(", ") || "Your golf holiday"
	const travelLabel = quote.travel_millis ? moment(quote.travel_millis).format("ddd D MMMM YYYY") : "Dates to be confirmed"
	const groupSize = Number(quote.group_size) || 0

	const setField = (field) => (event) => setPayer((current) => ({ ...current, [field]: event.target.value }))

	const detailsValid =
		payer.firstName.trim().length > 0 && payer.lastName.trim().length > 0 && EMAIL_RE.test(payer.email.trim())

	return (
		<div className="pq-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
			<div className="pq-modal" role="dialog" aria-modal="true" aria-label="Secure your booking">
				<div className="pq-modal-head">
					<h3>{stage === "done" ? "Booking confirmed" : "Secure your booking"}</h3>
					<button type="button" className="pq-modal-close" onClick={onClose} aria-label="Close">
						<X size={20} />
					</button>
				</div>

				<div className="pq-modal-body">
					{stage === "done" ? (
						<div className="pq-modal-success">
							<span className="pq-modal-success-icon">
								<Check size={30} />
							</span>
							<h4>Payment received</h4>
							<p>
								Thank you, {payer.firstName.trim() || "and welcome aboard"}! Your {gbp(deposit.dueToday)} deposit is paid and your trip
								is booked. We've emailed your receipt to {payer.email.trim()} and your sales agent will be in touch with the next
								steps.
							</p>
							<button type="button" className="pq-cta is-block" onClick={onClose}>
								Done
							</button>
						</div>
					) : (
						<>
							{/* Recap */}
							<div className="pq-modal-recap">
								<div className="pq-modal-recap-row">
									<span>Trip</span>
									<strong>{hotelTitle}</strong>
								</div>
								<div className="pq-modal-recap-row">
									<span>Travel</span>
									<strong>{travelLabel}</strong>
								</div>
								{groupSize > 0 && (
									<div className="pq-modal-recap-row">
										<span>Group</span>
										<strong>
											{groupSize} golfer{groupSize === 1 ? "" : "s"}
										</strong>
									</div>
								)}
								<div className="pq-modal-recap-row is-total">
									<span>Total trip price</span>
									<strong>{gbp(quote.sell_total)}</strong>
								</div>
								<div className="pq-modal-recap-row is-due">
									<span>{deposit.fullBalanceDue ? "Due today" : "Deposit due today"}</span>
									<strong>{gbp(deposit.dueToday)}</strong>
								</div>
							</div>

							{stage === "details" && (
								<>
									<div className="pq-modal-fields">
										<label className="pq-modal-field">
											<span>First name</span>
											<input type="text" value={payer.firstName} onChange={setField("firstName")} autoComplete="given-name" />
										</label>
										<label className="pq-modal-field">
											<span>Last name</span>
											<input type="text" value={payer.lastName} onChange={setField("lastName")} autoComplete="family-name" />
										</label>
										<label className="pq-modal-field is-wide">
											<span>Email</span>
											<input type="email" value={payer.email} onChange={setField("email")} autoComplete="email" />
										</label>
									</div>

									<button type="button" className="pq-cta is-block" disabled={!detailsValid} onClick={() => setStage("payment")}>
										<Lock size={17} />
										Continue to secure payment
									</button>
								</>
							)}

							{stage === "payment" && (
								<div className="pq-modal-payment">
									<DepositPayment
										token={quote.token}
										amount={deposit.dueToday}
										payer={payer}
										onBack={() => setStage("details")}
										onSuccess={() => setStage("done")}
									/>
								</div>
							)}

							<p className="pq-modal-secure">Payments are processed securely by Stripe. We never see or store your card details.</p>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

/** Stripe Elements wrapper for the deposit (card). */
function DepositPayment({ token, amount, payer, onBack, onSuccess }) {
	const elementsOptions = useMemo(
		() => ({
			mode: "payment",
			amount: Math.round(Number(amount || 0) * 100),
			currency: "gbp",
			paymentMethodTypes: ["card"],
			appearance: {
				theme: "stripe",
				variables: {
					colorPrimary: "#e41f13",
					fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
					borderRadius: "10px",
				},
			},
		}),
		[amount],
	)

	if (!stripePromise) {
		return (
			<div className="pq-modal-payment-placeholder">
				Card payment is temporarily unavailable. Please contact your sales agent to pay your deposit another way.
				<button type="button" className="pq-modal-back" onClick={onBack}>
					← Back to your details
				</button>
			</div>
		)
	}

	return (
		<Elements stripe={stripePromise} options={elementsOptions}>
			<DepositForm token={token} amount={amount} payer={payer} onBack={onBack} onSuccess={onSuccess} />
		</Elements>
	)
}

function DepositForm({ token, amount, payer, onBack, onSuccess }) {
	const stripe = useStripe()
	const elements = useElements()

	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState("")

	const pay = async () => {
		if (!stripe || !elements) return
		setSubmitting(true)
		setError("")

		// Validate the card fields client-side first.
		const submitResult = await elements.submit()
		if (submitResult.error) {
			setError(submitResult.error.message || "Please check your card details and try again.")
			setSubmitting(false)
			return
		}

		// Mint the deposit PaymentIntent server-side (the amount is recomputed
		// from the real quote there - the value shown here is never trusted).
		let clientSecret
		try {
			const response = await createQuoteDepositIntentCallable({
				token,
				payerFirstName: payer.firstName.trim(),
				payerLastName: payer.lastName.trim(),
				payerEmail: payer.email.trim(),
				returnUrl: typeof window !== "undefined" ? window.location.href : "",
			})
			clientSecret = response?.data?.clientSecret
			if (!clientSecret) throw new Error("No client secret returned.")
		} catch (err) {
			setError(err?.message || "Could not start the payment. Please try again.")
			setSubmitting(false)
			return
		}

		const confirmResult = await stripe.confirmPayment({
			elements,
			clientSecret,
			confirmParams: {
				return_url: typeof window !== "undefined" ? window.location.href : "",
				receipt_email: payer.email.trim(),
				payment_method_data: {
					billing_details: {
						name: `${payer.firstName} ${payer.lastName}`.trim(),
						email: payer.email.trim(),
					},
				},
			},
			redirect: "if_required",
		})

		if (confirmResult.error) {
			setError(confirmResult.error.message || "Payment could not be completed.")
			setSubmitting(false)
			return
		}

		if (confirmResult.paymentIntent?.status === "succeeded") {
			if (typeof onSuccess === "function") onSuccess(confirmResult.paymentIntent)
			return
		}

		setError("Additional authentication is required. Please try again or contact us if the problem persists.")
		setSubmitting(false)
	}

	return (
		<div className="pq-deposit-form">
			<PaymentElement
				options={{
					layout: "tabs",
					defaultValues: {
						billingDetails: {
							name: `${payer.firstName} ${payer.lastName}`.trim(),
							email: payer.email.trim(),
						},
					},
				}}
			/>

			{error && <p className="pq-deposit-error">{error}</p>}

			<button type="button" className="pq-cta is-block" onClick={pay} disabled={!stripe || submitting}>
				{submitting ? "Processing..." : `Pay £${gbp2(amount)} deposit`}
			</button>

			<button type="button" className="pq-modal-back" onClick={onBack} disabled={submitting}>
				← Back to your details
			</button>
		</div>
	)
}
